import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/* Wide enough for "Mark pending" at 15px without wrapping. */
const ACTION_WIDTH = 104
/* Under this the gesture is still ambiguous, so the browser keeps the scroll. */
const LOCK_SLOP = 10

function actionLabel(control) {
  const override = control.getAttribute('data-context-action')?.trim()
  return override || control.getAttribute('aria-label')?.trim() || control.textContent?.trim() || ''
}

/* The same discovery RowContextMenu uses: a row opts in with data-context-row,
   and the action is whatever control it already carries. Nothing is invented,
   so any confirmation on that control still applies. */
function swipeTargetFor(node) {
  const row = node?.closest?.('[data-context-row]')
  if (!row) return null
  const parent = row.parentElement
  if (!parent) return null
  const control = row.querySelector('[data-context-action]')
  if (
    !control ||
    control.disabled ||
    control.getAttribute('aria-disabled') === 'true'
  ) return null
  const label = actionLabel(control)
  if (!label) return null
  return { row, parent, control, label }
}

function place(row, offset, dragging) {
  row.style.transform = offset ? `translateX(${offset}px)` : ''
  row.classList.toggle('is-swiping', offset !== 0)
  row.classList.toggle('is-dragging', dragging)
}

export default function RowSwipe() {
  const [open, setOpen] = useState(null)
  const openRef = useRef(null)
  const gestureRef = useRef(null)

  const close = useCallback(() => {
    const current = openRef.current
    openRef.current = null
    if (current?.row.isConnected) place(current.row, 0, false)
    setOpen(null)
  }, [])

  useEffect(() => {
    const main = document.querySelector('.main')
    if (!main) return undefined

    function onTouchStart(event) {
      if (event.touches.length !== 1) return
      const target = swipeTargetFor(event.target)
      const current = openRef.current
      if (current && target?.row !== current.row) close()
      if (!target) {
        gestureRef.current = null
        return
      }
      const touch = event.touches[0]
      const from = current?.row === target.row ? -ACTION_WIDTH : 0
      gestureRef.current = {
        ...target,
        startX: touch.clientX,
        startY: touch.clientY,
        axis: null,
        from,
        offset: from,
      }
    }

    function onTouchMove(event) {
      const gesture = gestureRef.current
      if (!gesture || event.touches.length !== 1) return
      const touch = event.touches[0]
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY

      if (gesture.axis === null) {
        /* Vertical wins ties. A list scroll is never stolen by a row, which is
           how .tscroll swallowed vertical swipes before it was fixed. */
        if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < LOCK_SLOP) {
          if (Math.abs(dy) >= LOCK_SLOP) gestureRef.current = null
          return
        }
        gesture.axis = 'x'
        openRef.current = {
          row: gesture.row,
          parent: gesture.parent,
          control: gesture.control,
          label: gesture.label,
        }
        setOpen(openRef.current)
      }

      event.preventDefault()
      gesture.offset = Math.min(0, Math.max(-ACTION_WIDTH, gesture.from + dx))
      place(gesture.row, gesture.offset, true)
    }

    function onTouchEnd() {
      const gesture = gestureRef.current
      gestureRef.current = null
      if (!gesture || gesture.axis !== 'x') return
      const settled = gesture.offset <= -ACTION_WIDTH / 2
      place(gesture.row, settled ? -ACTION_WIDTH : 0, false)
      if (!settled) {
        openRef.current = null
        setOpen(null)
      }
    }

    main.addEventListener('touchstart', onTouchStart, { passive: true })
    main.addEventListener('touchmove', onTouchMove, { passive: false })
    main.addEventListener('touchend', onTouchEnd)
    main.addEventListener('touchcancel', onTouchEnd)
    main.addEventListener('scroll', close, { capture: true })
    return () => {
      main.removeEventListener('touchstart', onTouchStart)
      main.removeEventListener('touchmove', onTouchMove)
      main.removeEventListener('touchend', onTouchEnd)
      main.removeEventListener('touchcancel', onTouchEnd)
      main.removeEventListener('scroll', close, { capture: true })
      close()
    }
  }, [close])

  if (!open || !open.row.isConnected || !open.parent.isConnected) return null

  /* Measured rather than read off offsetTop, so a transformed row still lands
     the action on the right line. Only the vertical numbers are used. */
  const parentRect = open.parent.getBoundingClientRect()
  const rowRect = open.row.getBoundingClientRect()

  function activate() {
    const control = open.control
    close()
    window.requestAnimationFrame(() => {
      if (control.isConnected) control.click()
    })
  }

  return createPortal(
    <div
      className="swipe-action"
      style={{ top: rowRect.top - parentRect.top, height: rowRect.height, width: ACTION_WIDTH }}
    >
      <button type="button" onClick={activate}>{open.label}</button>
    </div>,
    open.parent,
  )
}
